import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";

export interface AIBehaviorSettings {
  suggestionFrequency: "minimal" | "balanced" | "aggressive";
  showInlineSuggestions: boolean;
  autoAcceptMinor: boolean;
}

export interface AutoSaveSettings {
  enabled: boolean;
  intervalSeconds: number;
}

export interface UserSettings {
  aiEnabled: boolean;
  autoSave: AutoSaveSettings;
  aiBehavior: AIBehaviorSettings;
}

interface SettingsContextType {
  settings: UserSettings;
  setAIEnabled: (enabled: boolean) => void;
  updateAutoSave: (settings: Partial<AutoSaveSettings>) => void;
  updateAIBehavior: (settings: Partial<AIBehaviorSettings>) => void;
  resetToDefaults: () => void;
}

const STORAGE_KEY = "verso-settings";

const DEFAULT_SETTINGS: UserSettings = {
  aiEnabled: true,
  autoSave: {
    enabled: true,
    intervalSeconds: 30,
  },
  aiBehavior: {
    suggestionFrequency: "balanced",
    showInlineSuggestions: true,
    autoAcceptMinor: false,
  },
};

const SettingsContext = createContext<SettingsContextType | null>(null);

function getStoredSettings(): UserSettings {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        aiEnabled: parsed.aiEnabled ?? DEFAULT_SETTINGS.aiEnabled,
        autoSave: { ...DEFAULT_SETTINGS.autoSave, ...parsed.autoSave },
        aiBehavior: { ...DEFAULT_SETTINGS.aiBehavior, ...parsed.aiBehavior },
      };
    }
  } catch {
    // Invalid JSON, use defaults
  }
  return DEFAULT_SETTINGS;
}

interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<UserSettings>(getStoredSettings);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const setAIEnabled = useCallback((enabled: boolean) => {
    setSettings((prev) => ({ ...prev, aiEnabled: enabled }));
  }, []);

  const updateAutoSave = useCallback((updates: Partial<AutoSaveSettings>) => {
    setSettings((prev) => ({
      ...prev,
      autoSave: { ...prev.autoSave, ...updates },
    }));
  }, []);

  const updateAIBehavior = useCallback((updates: Partial<AIBehaviorSettings>) => {
    setSettings((prev) => ({
      ...prev,
      aiBehavior: { ...prev.aiBehavior, ...updates },
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const value = useMemo<SettingsContextType>(
    () => ({
      settings,
      setAIEnabled,
      updateAutoSave,
      updateAIBehavior,
      resetToDefaults,
    }),
    [settings, setAIEnabled, updateAutoSave, updateAIBehavior, resetToDefaults],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
